import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtIQD, formatErr } from "../lib/api";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Truck, Ticket, X } from "lucide-react";
import RequireAuth from "../components/RequireAuth";

export default function Checkout() {
  return <RequireAuth><CheckoutInner /></RequireAuth>;
}

function CheckoutInner() {
  const { items, total: subtotal, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [govs, setGovs] = useState([]);
  const [form, setForm] = useState({
    full_name: "", phone: "", governorate: "", city: "", address: "", landmark: "", notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [orderNo, setOrderNo] = useState("");
  const [confirmedTotals, setConfirmedTotals] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, discount }
  const [applying, setApplying] = useState(false);

  useEffect(() => { api.get("/governorates").then((r) => setGovs(r.data)); }, []);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: user.full_name || "", phone: user.phone || "" }));
  }, [user]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const selectedGov = govs.find((g) => g.name === form.governorate);
  const rawDeliveryFee = selectedGov?.delivery_fee || 0;
  const freeDeliveryEnabled = settings?.free_delivery_enabled === true;
  const threshold = freeDeliveryEnabled ? Number(settings?.free_delivery_threshold || 0) : 0;
  const isFreeShipping = threshold > 0 && subtotal >= threshold;
  const deliveryFee = isFreeShipping ? 0 : rawDeliveryFee;
  const discount = coupon?.discount || 0;
  const total = Math.max(0, subtotal - discount) + deliveryFee;

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code || applying) return;
    setApplying(true);
    try {
      const r = await api.post("/coupons/validate", { code, subtotal });
      setCoupon(r.data);
      toast.success(`تم تطبيق كود ${r.data.code}`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "كود غير صالح"); }
    finally { setApplying(false); }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!items.length) return toast.error("السلة فارغة");
    if (!form.full_name || !form.phone || !form.governorate || !form.city || !form.address) {
      return toast.error("يرجى تعبئة كل الحقول المطلوبة");
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        coupon_code: coupon?.code || "",
        items: items.map((it) => ({ product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity })),
      };
      const r = await api.post("/orders/checkout", payload);
      setOrderNo(r.data.order_number);
      setConfirmedTotals({ subtotal: r.data.subtotal, delivery_fee: r.data.delivery_fee, discount: r.data.discount, total: r.data.total });
      clear();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "فشل إنشاء الطلب");
    } finally { setSubmitting(false); }
  };

  if (orderNo) return (
    <div className="App">
      <Header />
      <div className="pt-32 pb-20 pg-container text-center max-w-xl mx-auto">
        <CheckCircle2 size={80} className="mx-auto text-green-400 mb-6" />
        <h1 className="text-3xl md:text-4xl font-black mb-3">تم إنشاء طلبك بنجاح</h1>
        <p className="text-gray-400 mb-6">سنتواصل معك قريباً لتأكيد التوصيل.</p>
        <div className="bg-[#24262B] border border-[#3A3D43] rounded-lg p-6 mb-6">
          <div className="text-xs text-gray-400 mb-1">رقم الطلب</div>
          <div data-testid="order-number" className="text-2xl font-black text-[#EF1B17]">{orderNo}</div>
          {confirmedTotals && (
            <div className="mt-4 text-sm space-y-1 text-right">
              <div className="flex justify-between"><span className="text-gray-400">المنتجات</span><span>{fmtIQD(confirmedTotals.subtotal)}</span></div>
              {(confirmedTotals.discount || 0) > 0 && (
                <div className="flex justify-between text-green-400"><span>خصم كود</span><span>-{fmtIQD(confirmedTotals.discount)}</span></div>
              )}
              <div className="flex justify-between"><span className="text-gray-400">التوصيل</span><span>{fmtIQD(confirmedTotals.delivery_fee)}</span></div>
              <div className="flex justify-between font-black text-base pt-2 border-t border-[#3A3D43] mt-2"><span>الإجمالي</span><span className="text-[#EF1B17]">{fmtIQD(confirmedTotals.total)}</span></div>
            </div>
          )}
        </div>
        <button onClick={() => nav("/")} className="bg-[#EF1B17] hover:bg-[#FF3B30] text-white font-bold px-8 py-3 rounded-md">العودة للرئيسية</button>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="App">
      <Header />
      <div className="pg-page-top pb-16">
        <div className="pg-container">
          <h1 className="text-3xl md:text-4xl font-black mb-8">إتمام الشراء</h1>
          <form onSubmit={submit} className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4 bg-[#24262B] border border-[#3A3D43] rounded-lg p-6">
              <h3 className="font-black text-lg mb-2">معلومات التوصيل</h3>
              <Field label="الاسم الكامل *"><input data-testid="co-name" value={form.full_name} onChange={onChange("full_name")} className={inputCls} required /></Field>
              <Field label="رقم الهاتف *"><input data-testid="co-phone" value={form.phone} onChange={onChange("phone")} placeholder="07XXXXXXXXX" className={inputCls} required /></Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="المحافظة *">
                  <Select value={form.governorate} onValueChange={(v) => setForm((f) => ({ ...f, governorate: v }))}>
                    <SelectTrigger data-testid="co-gov" className="bg-[#191A1D] border-[#3A3D43]"><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent className="bg-[#24262B] border-[#3A3D43] text-[#F4F4F2]">
                      {govs.map((g) => (
                        <SelectItem key={g.id} value={g.name}>
                          <span className="inline-flex items-center gap-2">{g.name} <span className="text-[10px] text-gray-400">({fmtIQD(g.delivery_fee || 0)})</span></span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="المدينة / المنطقة *"><input data-testid="co-city" value={form.city} onChange={onChange("city")} className={inputCls} required /></Field>
              </div>
              <Field label="العنوان الكامل *"><input data-testid="co-address" value={form.address} onChange={onChange("address")} className={inputCls} required /></Field>
              <Field label="أقرب معلم (اختياري)"><input data-testid="co-landmark" value={form.landmark} onChange={onChange("landmark")} className={inputCls} /></Field>
              <Field label="ملاحظات (اختياري)"><textarea data-testid="co-notes" value={form.notes} onChange={onChange("notes")} rows={3} className={inputCls} /></Field>
            </div>

            <div className="bg-[#24262B] border border-[#3A3D43] rounded-lg p-6 h-fit sticky top-32">
              <h3 className="font-black text-lg mb-4">ملخص الطلب</h3>
              <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mb-4">
                {items.map((it) => (
                  <div key={it.key} className="flex justify-between text-sm border-b border-[#3A3D43]/60 pb-2">
                    <div className="min-w-0">
                      <div className="font-bold line-clamp-1">{it.name}</div>
                      <div className="text-xs text-gray-400">{it.variant_name} × {it.quantity}</div>
                    </div>
                    <div className="font-bold shrink-0">{fmtIQD(it.unit_price * it.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5 text-sm mb-3">
                <div className="flex justify-between"><span className="text-gray-400">المنتجات</span><span>{fmtIQD(subtotal)}</span></div>
                {coupon && (
                  <div className="flex justify-between text-green-400">
                    <span className="inline-flex items-center gap-1"><Ticket size={12} /> {coupon.code}</span>
                    <span data-testid="co-discount">-{fmtIQD(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400 inline-flex items-center gap-1"><Truck size={12} /> التوصيل</span>
                  {isFreeShipping ? (
                    <span data-testid="co-delivery-fee" className="text-green-400 font-bold">مجاني ✓</span>
                  ) : (
                    <span data-testid="co-delivery-fee">{selectedGov ? fmtIQD(deliveryFee) : "—"}</span>
                  )}
                </div>
                {threshold > 0 && !isFreeShipping && (
                  <div data-testid="co-free-hint" className="text-[11px] text-[#EF1B17]">
                    أضف {fmtIQD(threshold - subtotal)} للحصول على توصيل مجاني
                  </div>
                )}
              </div>

              {/* Coupon input */}
              {!coupon ? (
                <div className="flex gap-2 mb-3">
                  <input
                    data-testid="co-coupon-input"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}
                    placeholder="كود خصم"
                    className={inputCls + " font-mono flex-1"}
                  />
                  <button
                    type="button"
                    data-testid="co-coupon-apply"
                    onClick={applyCoupon} disabled={applying || !couponInput}
                    className="bg-[#3A3D43] hover:bg-[#4a4d55] disabled:opacity-50 text-white text-sm font-bold px-4 rounded-md"
                  >
                    {applying ? "..." : "تطبيق"}
                  </button>
                </div>
              ) : (
                <div className="mb-3 flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2">
                  <span className="text-sm text-green-400 font-bold inline-flex items-center gap-1"><Ticket size={14} /> {coupon.code}</span>
                  <button type="button" data-testid="co-coupon-remove" onClick={removeCoupon} className="text-green-400 hover:text-red-400"><X size={14} /></button>
                </div>
              )}
              <div className="flex justify-between text-lg font-black mb-3 pt-3 border-t border-[#3A3D43]">
                <span>الإجمالي</span><span data-testid="co-total" className="text-[#EF1B17]">{fmtIQD(total)}</span>
              </div>
              <div className="bg-[#191A1D] border border-[#3A3D43] rounded-md p-3 text-xs text-gray-300 mb-4">
                💵 الدفع عند الاستلام (نقداً)
              </div>
              <button
                data-testid="place-order-btn"
                disabled={submitting || items.length === 0}
                className="w-full bg-[#EF1B17] hover:bg-[#FF3B30] disabled:bg-[#3A3D43] text-white font-bold py-3 rounded-md pg-lift"
              >
                {submitting ? "جاري إرسال الطلب..." : "تأكيد الطلب"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const inputCls = "w-full bg-[#191A1D] border border-[#3A3D43] rounded-md px-3 py-2.5 text-sm focus:border-[#EF1B17] focus:outline-none";
function Field({ label, children }) {
  return <label className="block"><div className="text-xs font-bold text-gray-400 mb-1">{label}</div>{children}</label>;
}
